import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { resetUserSignup } from '../Services/UserService.ts';

// Admin "Reset sign up" for one member who never finished. Pick what they should sign up to (or leave it
// on their original invitation), send, and get the fresh link back so it can also be shared by hand.
const ResetSignupModal = ({ member, venueKey, plans, onClose }) => {
    const [planId, setPlanId] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState(null);
    const [copied, setCopied] = useState(false);

    const send = async () => {
        setBusy(true);
        setError('');
        try {
            const data = await resetUserSignup(venueKey, member.userId, planId ? parseInt(planId, 10) : null);
            setResult(data);
        } catch (e) {
            setError(e.message || 'Could not reset the sign up.');
        } finally {
            setBusy(false);
        }
    };

    const fullLink = result?.inviteUrl ? `${window.location.origin}${result.inviteUrl}` : '';

    const copy = () => {
        navigator.clipboard.writeText(fullLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="card-luxury w-full max-w-lg p-6">
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <h3 className="text-lg font-display font-semibold text-accent-platinum">Send sign up link</h3>
                        <p className="text-sm text-emerald-600/70">To {member.name} ({member.email})</p>
                    </div>
                    <button onClick={onClose} className="text-emerald-600 hover:text-emerald-400" aria-label="Close">
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>

                {!result ? (
                    <>
                        <p className="text-sm text-gray-300 mb-4">
                            This member has an account but no subscription. They get an email with a fresh link back to the start of their invitation.
                            They log in and go straight to the Direct Debit step. Nothing is charged until they finish it.
                        </p>

                        <label className="block text-sm font-semibold text-emerald-300/80 mb-2">What should they sign up to?</label>
                        <select
                            value={planId}
                            onChange={(e) => setPlanId(e.target.value)}
                            className="input-field w-full mb-4"
                        >
                            <option value="">Their original invitation (if we can find it)</option>
                            {plans.map((plan) => (
                                <option key={plan.id} value={plan.id}>
                                    {plan.name} (£{Number(plan.subscriptionCycleCost || 0).toFixed(2)})
                                </option>
                            ))}
                        </select>

                        {error && (
                            <div className="mb-4 p-3 rounded-lg border border-performance-pulse/40 bg-performance-pulse/10 text-sm text-performance-pulse">
                                {error}
                            </div>
                        )}

                        <div className="flex justify-end gap-3">
                            <button onClick={onClose} className="btn-outline">Cancel</button>
                            <button onClick={send} disabled={busy} className="btn-primary disabled:opacity-50">
                                {busy ? 'Sending…' : 'Send link'}
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="mb-4 p-3 rounded-lg border border-emerald-500/40 bg-emerald-900/20 text-sm text-emerald-300">
                            {result.message}
                            {result.planName && <span className="block mt-1 text-emerald-400/80">Plan: {result.planName}</span>}
                        </div>
                        {fullLink && (
                            <div className="mb-4">
                                <p className="text-xs text-emerald-600/70 mb-1">Link (single use, valid for 14 days)</p>
                                <div className="flex items-center gap-2">
                                    <code className="flex-1 text-xs break-all text-emerald-300 bg-luxury-obsidian rounded-lg p-2">{fullLink}</code>
                                    <button onClick={copy} className="btn-outline whitespace-nowrap">{copied ? 'Copied' : 'Copy'}</button>
                                </div>
                            </div>
                        )}
                        <div className="flex justify-end">
                            <button onClick={onClose} className="btn-primary">Done</button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ResetSignupModal;
